import React, { useState, useRef, useEffect } from 'react';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatInTimezone, parseDateInTimezone } from '@/utils/timezone';

interface TimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  format?: '12h' | '24h';
  minTime?: string;
  maxTime?: string;
  timezone?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  value = '',
  onChange,
  placeholder = 'Select time',
  disabled = false,
  className,
  format = '12h',
  minTime,
  maxTime,
  timezone,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState('12');
  const [minutes, setMinutes] = useState('00');
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');
  const [focusedInput, setFocusedInput] = useState<'hours' | 'minutes' | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef<HTMLInputElement>(null);

  // Parse initial value
  useEffect(() => {
    if (value) {
      const time = parseTimeValue(value);
      if (time) {
        setHours(time.hours);
        setMinutes(time.minutes);
        setPeriod(time.period);
      }
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const parseTimeValue = (timeStr: string): { hours: string; minutes: string; period: 'AM' | 'PM' } | null => {
    if (!timeStr) return null;
    
    // Handle 12h format with AM/PM (e.g., "09:00 AM", "12:00 PM")
    if (timeStr.includes(' ') && (timeStr.includes('AM') || timeStr.includes('PM'))) {
      const [timePart, periodPart] = timeStr.split(' ');
      const [h, m] = timePart.split(':');
      const hour12 = parseInt(h, 10);
      const mins = m.padStart(2, '0');
      const period = periodPart as 'AM' | 'PM';
      
      return {
        hours: hour12.toString().padStart(2, '0'),
        minutes: mins,
        period
      };
    }
    
    // Handle 24h format (e.g., "09:00", "15:00")
    if (timeStr.includes(':')) {
      const [h, m] = timeStr.split(':');
      const hour24 = parseInt(h, 10);
      const mins = m.padStart(2, '0');
      
      if (format === '12h') {
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const period: 'AM' | 'PM' = hour24 >= 12 ? 'PM' : 'AM';
        return {
          hours: hour12.toString().padStart(2, '0'),
          minutes: mins,
          period
        };
      } else {
        return {
          hours: h.padStart(2, '0'),
          minutes: mins,
          period: 'AM' as const
        };
      }
    }
    
    return null;
  };

  const formatTimeValue = (h: string, m: string, p: 'AM' | 'PM') => {
    if (format === '12h') {
      return `${h}:${m} ${p}`;
    } else {
      const hour24 = p === 'PM' && h !== '12' ? parseInt(h, 10) + 12 : 
                    p === 'AM' && h === '12' ? 0 : parseInt(h, 10);
      return `${hour24.toString().padStart(2, '0')}:${m}`;
    }
  };

  const handleTimeChange = (newHours: string, newMinutes: string, newPeriod: 'AM' | 'PM') => {
    const timeStr = formatTimeValue(newHours, newMinutes, newPeriod);
    onChange?.(timeStr);
  };

  const handleHoursChange = (newHours: string) => {
    const numHours = parseInt(newHours, 10);
    if (numHours >= 1 && numHours <= 12) {
      const paddedHours = newHours.padStart(2, '0');
      setHours(paddedHours);
      handleTimeChange(paddedHours, minutes, period);
    } else if (newHours === '' || newHours === '0') {
      // Allow empty input for user to type
      setHours(newHours);
    }
  };

  const handleMinutesChange = (newMinutes: string) => {
    const numMinutes = parseInt(newMinutes, 10);
    if (numMinutes >= 0 && numMinutes <= 59) {
      const paddedMinutes = newMinutes.padStart(2, '0');
      setMinutes(paddedMinutes);
      handleTimeChange(hours, paddedMinutes, period);
    } else if (newMinutes === '') {
      // Allow empty input for user to type
      setMinutes(newMinutes);
    }
  };

  const handlePeriodChange = (newPeriod: 'AM' | 'PM') => {
    setPeriod(newPeriod);
    handleTimeChange(hours, minutes, newPeriod);
  };

  const incrementHours = () => {
    const currentHours = parseInt(hours, 10);
    const newHours = currentHours === 12 ? 1 : currentHours + 1;
    handleHoursChange(newHours.toString().padStart(2, '0'));
  };

  const decrementHours = () => {
    const currentHours = parseInt(hours, 10);
    const newHours = currentHours === 1 ? 12 : currentHours - 1;
    handleHoursChange(newHours.toString().padStart(2, '0'));
  };

  const incrementMinutes = () => {
    const currentMinutes = parseInt(minutes, 10);
    const newMinutes = currentMinutes === 59 ? 0 : currentMinutes + 1;
    handleMinutesChange(newMinutes.toString().padStart(2, '0'));
  };

  const decrementMinutes = () => {
    const currentMinutes = parseInt(minutes, 10);
    const newMinutes = currentMinutes === 0 ? 59 : currentMinutes - 1;
    handleMinutesChange(newMinutes.toString().padStart(2, '0'));
  };

  const displayValue = value || placeholder;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          "w-full rounded border-l-[2.4px] border-primary px-5 py-3 flex justify-between items-center text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm outline-none transition-colors",
          disabled && "opacity-50 cursor-not-allowed",
          !disabled && "hover:bg-[#E8F2FF]"
        )}
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span className={value ? "text-[#1A1A1A]" : "text-[#9CA3AF]"}>
            {displayValue}
          </span>
        </div>
        <ChevronDown className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-center gap-4">
            {/* Hours */}
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={incrementHours}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <input
                ref={hoursRef}
                type="text"
                value={hours}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 2) {
                    setHours(val);
                    if (val.length === 2) {
                      const numHours = parseInt(val, 10);
                      if (numHours >= 1 && numHours <= 12) {
                        handleHoursChange(val);
                      }
                    }
                  }
                }}
                onFocus={() => setFocusedInput('hours')}
                onBlur={() => {
                  setFocusedInput(null);
                  handleHoursChange(hours);
                }}
                className="w-12 text-center text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:outline-none"
                maxLength={2}
              />
              <button
                type="button"
                onClick={decrementHours}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <span className="text-xs text-gray-500">Hours</span>
            </div>

            <div className="text-2xl font-bold text-gray-400">:</div>

            {/* Minutes */}
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={incrementMinutes}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <input
                ref={minutesRef}
                type="text"
                value={minutes}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 2) {
                    setMinutes(val);
                    if (val.length === 2) {
                      const numMinutes = parseInt(val, 10);
                      if (numMinutes >= 0 && numMinutes <= 59) {
                        handleMinutesChange(val);
                      }
                    }
                  }
                }}
                onFocus={() => setFocusedInput('minutes')}
                onBlur={() => {
                  setFocusedInput(null);
                  handleMinutesChange(minutes);
                }}
                className="w-12 text-center text-lg font-semibold border border-gray-300 rounded px-2 py-1 focus:outline-none"
                maxLength={2}
              />
              <button
                type="button"
                onClick={decrementMinutes}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              <span className="text-xs text-gray-500">Minutes</span>
            </div>

            {/* AM/PM (only for 12h format) */}
            {format === '12h' && (
              <>
                <div className="text-2xl font-bold text-gray-400">:</div>
                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePeriodChange('AM')}
                    className={cn(
                      "px-3 py-1 rounded text-sm font-medium transition-colors",
                      period === 'AM' 
                        ? "bg-primary text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePeriodChange('PM')}
                    className={cn(
                      "px-3 py-1 rounded text-sm font-medium transition-colors",
                      period === 'PM' 
                        ? "bg-primary text-white" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                  >
                    PM
                  </button>
                  <span className="text-xs text-gray-500">Period</span>
                </div>
              </>
            )}
          </div>

          {/* Quick time buttons */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-2">
              {['09:00 AM', '12:00 PM', '03:00 PM', '06:00 PM', '09:00 PM'].map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => {
                    const parsed = parseTimeValue(time);
                    if (parsed) {
                      setHours(parsed.hours);
                      setMinutes(parsed.minutes);
                      setPeriod(parsed.period);
                      handleTimeChange(parsed.hours, parsed.minutes, parsed.period);
                    }
                  }}
                  className="px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
